
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Paper,
    Divider,
    Chip,
    CircularProgress,
    alpha
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { fetchHolidays, getUpcomingHolidays } from '../../utils/holidayUtils';

const MarketHolidaysWidget = ({ title = "Market Holidays 2026", showTabs = true, radius }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [holidays, setHolidays] = useState([]);
    const [error, setError] = useState(null);

    const exchanges = [
        { name: 'NSE', file: 'NSE_Holidays_2026.csv', color: '#1976d2' },
        { name: 'BSE', file: 'BSE_Holidays_2026.csv', color: '#ed6c02' },
        { name: 'MCX', file: 'MCX_Holidays_2026.csv', color: '#9c27b0' }
    ];

    useEffect(() => {
        const loadHolidays = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchHolidays(exchanges[activeTab].file);
                if (data && data.length > 0) {
                    const upcoming = getUpcomingHolidays(data, 10);
                    setHolidays(upcoming);
                } else {
                    setHolidays([]);
                }
            } catch (err) {
                setError("Failed to load holiday data.");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadHolidays();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: radius || { xs: 2, md: 3 },
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(229, 231, 235, 0.5)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                <CalendarMonthIcon sx={{ color: exchanges[activeTab].color }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {title}
                </Typography>
            </Box>

            {showTabs && (
                // Flex button group instead of MUI <Tabs variant="fullWidth">: the
                // tab scroller hid overflow with no scroll buttons, so the 3rd
                // exchange (MCX) was clipped and unreachable on narrower cards /
                // smaller screens. flex + flexWrap keeps all three exchanges visible
                // at any width (they wrap to a second row instead of being clipped).
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {exchanges.map((exchange, index) => {
                        const selected = activeTab === index;
                        return (
                            <Box
                                key={exchange.name}
                                component="button"
                                type="button"
                                onClick={() => handleTabChange(null, index)}
                                aria-pressed={selected}
                                sx={{
                                    flex: '1 1 0',
                                    minWidth: 56,
                                    py: 1,
                                    px: 1.5,
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    transition: 'all 0.25s ease',
                                    color: selected ? '#fff' : '#6b7280',
                                    backgroundColor: selected ? exchange.color : 'transparent',
                                    '&:hover': {
                                        backgroundColor: selected ? exchange.color : alpha(exchange.color, 0.08),
                                    },
                                }}
                            >
                                {exchange.name}
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
                        <CircularProgress size={30} sx={{ color: exchanges[activeTab].color }} />
                    </Box>
                ) : error ? (
                    <Typography color="error" variant="body2" textAlign="center" sx={{ py: 3 }}>
                        {error}
                    </Typography>
                ) : holidays.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
                        No upcoming holidays found.
                    </Typography>
                ) : (
                    <List disablePadding>
                        {holidays.map((holiday, index) => (
                            <React.Fragment key={index}>
                                <ListItem
                                    sx={{
                                        px: 0,
                                        py: 1.5,
                                        transition: 'transform 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateX(5px)'
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 45,
                                            height: 45,
                                            borderRadius: '10px',
                                            bgcolor: alpha(exchanges[activeTab].color, 0.1),
                                            color: exchanges[activeTab].color,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 2,
                                            flexShrink: 0
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, lineHeight: 1 }}>
                                            {new Date(holiday.Date).toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}
                                        </Typography>
                                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, lineHeight: 1 }}>
                                            {new Date(holiday.Date).getDate()}
                                        </Typography>
                                    </Box>
                                    <ListItemText
                                        primary={holiday['Holiday Occasion']}
                                        secondary={`${holiday.Day} • ${holiday['Open/Close']}`}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            fontWeight: 600,
                                            sx: { color: '#1f2937' }
                                        }}
                                        secondaryTypographyProps={{
                                            variant: 'caption',
                                            sx: { color: '#6b7280' }
                                        }}
                                    />
                                    {holiday['Open/Close'] === 'Closed' && (
                                        <Chip
                                            label="Closed"
                                            size="small"
                                            sx={{
                                                height: '20px',
                                                fontSize: '0.65rem',
                                                bgcolor: alpha('#ef4444', 0.1),
                                                color: '#ef4444',
                                                fontWeight: 700,
                                                border: 'none'
                                            }}
                                        />
                                    )}
                                </ListItem>
                                {index < holidays.length - 1 && <Divider component="li" sx={{ opacity: 0.5 }} />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Box>
        </Paper>
    );
};

export default MarketHolidaysWidget;
